// Severity registry + helpers (no JSX — see Severity.tsx for the icon components).
//
// Maps Zabbix's six trigger severities (0..5) plus a non-alert "ok"/Normal
// state to a shaped, filled icon spec. Each level is set apart by SHAPE and
// FILL, not colour alone — circle -> triangle -> octagon, outline -> solid — so
// the icons stay legible in greyscale and for colour-blind users.

export type SeverityShape = 'circle' | 'triangle' | 'octagon'
export type SeverityFill = 'solid' | 'outline'

export interface SeverityMeta {
  label: string
  color: string
  shape: SeverityShape
  fill: SeverityFill
  glyph: string
  blurb: string
}

/** Canonical severity key: the string "ok" or a Zabbix level 0..5. */
export type SeverityKey = 'ok' | 0 | 1 | 2 | 3 | 4 | 5

export const SEVERITY: Record<SeverityKey, SeverityMeta> = {
  ok: { label: 'Normal', color: '#3FB950', shape: 'circle', fill: 'solid', glyph: 'check', blurb: 'No action needed' },
  0: { label: 'Not classified', color: '#97AAB3', shape: 'circle', fill: 'outline', glyph: '?', blurb: 'Unscored — needs triage' },
  1: { label: 'Information', color: '#7499FF', shape: 'circle', fill: 'outline', glyph: 'i', blurb: 'FYI — no action needed' },
  2: { label: 'Warning', color: '#FFC859', shape: 'circle', fill: 'outline', glyph: '!', blurb: 'Keep an eye out' },
  3: { label: 'Average', color: '#FFA059', shape: 'triangle', fill: 'outline', glyph: '!', blurb: 'Worth attention soon' },
  4: { label: 'High', color: '#E97659', shape: 'triangle', fill: 'solid', glyph: '!', blurb: 'Act soon — impact likely' },
  // Sev 5 is "Disaster" — Zabbix's own name. "Critical" is reserved for the
  // fleet-health BAND (High + Disaster) so band word and level label never collide.
  5: { label: 'Disaster', color: '#E45959', shape: 'octagon', fill: 'solid', glyph: '!', blurb: 'Act now' },
}

// Accept the level as a number (0..5), the string "ok", or a severity name.
const ALIASES: Record<string, SeverityKey> = {
  healthy: 'ok', normal: 'ok', good: 'ok', okay: 'ok', ok: 'ok',
  'not classified': 0, notclassified: 0, unclassified: 0, unscored: 0,
  information: 1, info: 1,
  warning: 2, warn: 2,
  average: 3,
  high: 4,
  disaster: 5, critical: 5,
}

export function normalizeSeverity(value: unknown): SeverityKey | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number' && value in SEVERITY) return value as SeverityKey
  const s = String(value).trim().toLowerCase()
  if (s === 'ok') return 'ok'
  if (/^[0-5]$/.test(s)) return Number(s) as SeverityKey
  if (s in ALIASES) return ALIASES[s]
  return null
}

export function severityMeta(value: unknown): SeverityMeta | null {
  const key = normalizeSeverity(value)
  return key === null ? null : SEVERITY[key]
}

// Colour-only severity dots the model still tends to reach for, mapped to the
// matching level — used to rewrite emoji into shaped icons wherever they slip
// through (prose, table cells).
export const EMOJI_SEV: Record<string, string> = {
  '🔴': '5', '🟥': '5', '🟠': '4', '🟧': '4', '🟡': '2', '🟨': '2',
  '🟢': 'ok', '🟩': 'ok', '🔵': '1', '🟦': '1', '⚪': '0', '⬜': '0',
  '🟣': '3', '🟪': '3',
}

// Matches a severity emoji OR a `sev:N` token (N = 0..5 or ok) anywhere in text.
// Exported as a source string so callers build a fresh (stateful) regex per use
// rather than sharing one global instance's lastIndex.
export const INLINE_SEV_SOURCE = '🔴|🟥|🟠|🟧|🟡|🟨|🟢|🟩|🔵|🟦|⚪|⬜|🟣|🟪|sev:(?:ok|[0-5])'
